import { Link } from "@tanstack/react-router";
import type { CSSProperties, ReactNode } from "react";
import { useMerchant, useWorkspaceId } from "@/providers/MerchantProvider";

export type CollaborationKind = "target" | "open";

interface CollaborationLinkProps {
  kind: CollaborationKind;
  collaborationId: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

/**
 * Links to a collaboration detail page while preserving the current workspace
 * context. The detail routes exist under both `/merchant/$merchantId/...` and
 * `/workspace/$workspaceId/merchant/$merchantId/...`; linking to the bare
 * merchant path from inside a workspace drops the `/workspace/...` prefix, and
 * `merchantRoute.beforeLoad` then rejects the merchant (it validates only direct
 * `user.merchants`, not workspace merchants) and redirects elsewhere — so the
 * collaboration opened the wrong page / errored (UNB-2097).
 *
 * Opens in a new tab, like the other links in the activity feed.
 */
export const CollaborationLink = ({ kind, collaborationId, className, style, children }: CollaborationLinkProps) => {
  const merchantId = useMerchant();
  const workspaceId = useWorkspaceId();

  const base = workspaceId ? "/workspace/$workspaceId/merchant/$merchantId" : "/merchant/$merchantId";

  // Built as an object and spread so TanStack accepts the dynamic `to` string
  // (same loose-path pattern the sidebar uses for workspace/merchant nav).
  const linkProps = {
    to: `${base}/collaborations/${kind}/$collaborationId`,
    params: workspaceId ? { workspaceId, merchantId, collaborationId } : { merchantId, collaborationId },
  };

  return (
    <Link {...linkProps} target="_blank" rel="noopener noreferrer" className={className} style={style}>
      {children}
    </Link>
  );
};
